import { useFieldArray, useFormContext } from 'react-hook-form';
import {
    PosMicrosIntegrationExternalPaymentTypes,
    PosMicrosIntegrationTransmissionProtocol,
} from '@mews-commander/data-access';
import { EnumSelectField, SelectField } from '@mews-commander/form';
import { FormInputField, lt, FormCheckboxField, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { Grid, Stack } from '@optimus-web/core';
import { useAccountingCategoriesOptions } from '../../hooks/useAccountingCategoriesOptions';
import { FieldNames } from '../fieldNames';
import { XmlPosOutletFormSchema } from '../microsOutlets/formProps';

interface PaymentMappingFormProps {
    index: number;
    formFormat?: string;
}

export const PaymentMappingForm = ({ index, formFormat }: PaymentMappingFormProps) => {
    const posIntegrationsServiceKeyEnabled = useFeatureFlag(FeatureFlag.posIntegrationsServiceKeyEnabled);
    const { setValue } = useFormContext<XmlPosOutletFormSchema>();
    const { accountingCategoriesOptions } = useAccountingCategoriesOptions();
    const paymentMappingIndex = `${FieldNames.paymentMappings}.${index}`;
    const { fields } = useFieldArray<XmlPosOutletFormSchema, 'paymentMappings'>({
        name: FieldNames.paymentMappings,
    });

    const handleOnChangeDefault = (checked: boolean) => {
        fields.forEach((_field, indexField) => {
            if (indexField !== index || !checked) {
                setValue(`${FieldNames.paymentMappings}.${indexField}.${FieldNames.isDefault}`, false, {
                    shouldDirty: true,
                }); // false
            }
        });
    };

    return (
        <Stack vertical spacing="200">
            {formFormat &&
                formFormat === PosMicrosIntegrationTransmissionProtocol.XmlPos &&
                posIntegrationsServiceKeyEnabled && (
                    <FormCheckboxField
                        name={`${paymentMappingIndex}.${FieldNames.isDefault}`}
                        onChange={(event, checked) => {
                            handleOnChangeDefault(checked);
                        }}
                    >
                        {lt('Default')}
                    </FormCheckboxField>
                )}
            <Grid
                columnGap="200"
                templateRows="repeat(auto-fit, minmax(0, 2fr), minmax(0, 2fr), minmax(0, 2fr))"
                templateColumns="minmax(0, 2fr) minmax(0, 2fr) minmax(0, 2fr)"
                height="100%"
                tabletOnly={{
                    templateColumns: 'minmax(0, 3fr) minmax(0, 3fr)',
                    templateRows: 'repeat(auto-fit, minmax(0, 3fr), minmax(0, 3fr))',
                }}
                largePhoneOnly={{
                    templateColumns: 'minmax(0, 2fr)',
                }}
                phoneOnly={{
                    templateColumns: 'minmax(0, 1fr)',
                }}
            >
                <FormInputField
                    name={`${paymentMappingIndex}.${FieldNames.paymentNumber}`}
                    label={lt('PosPaymentId')}
                    required
                />
                <FormInputField
                    name={`${paymentMappingIndex}.${FieldNames.paymasterId}`}
                    label={lt('PaymasterAccount')}
                />
                <SelectField
                    name={`${paymentMappingIndex}.${FieldNames.accountingCategoryId}`}
                    label={lt('AccountingCategoryKey')}
                    required
                    showEmptyValue={false}
                    options={accountingCategoriesOptions}
                />
                <EnumSelectField
                    name={`${paymentMappingIndex}.${FieldNames.externalPaymentType}`}
                    label={lt('ExternalPayment')}
                    enumClass={PosMicrosIntegrationExternalPaymentTypes}
                    enumPrefix="ExternalPaymentTypes"
                    required
                />
            </Grid>
        </Stack>
    );
};
